import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Handle "Show More" button if present
    const { showMoreBtn } = this.refs;
    if (showMoreBtn) {
      showMoreBtn.addEventListener('click', this.#handleShowMore);
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    
    const { showMoreBtn } = this.refs;
    if (showMoreBtn) {
      showMoreBtn.removeEventListener('click', this.#handleShowMore);
    }
  }

  /**
   * Handles the "Show More" button click
   */
  #handleShowMore = async () => {
    const { showMoreBtn } = this.refs;
    
    if (!showMoreBtn) return;

    // Disable button during loading
    const defaultText = showMoreBtn.dataset.textDefault || 'Show More';
    const loadingText = showMoreBtn.dataset.textLoading || 'Loading...';
    
    showMoreBtn.disabled = true;
    showMoreBtn.textContent = loadingText;

    try {
      // Load next page
      await this.loadNextPage();

      // Check if there are more pages
      const { grid } = this.refs;
      const { cards } = this.refs;
      
      if (grid && Array.isArray(cards) && cards.length > 0) {
        const lastCard = cards[cards.length - 1];
        const currentPage = Number(lastCard.dataset.page);
        const lastPage = Number(grid.dataset.lastPage);

        if (currentPage >= lastPage) {
          // Hide button if no more pages
          const buttonContainer = showMoreBtn.closest('.collection-show-more');
          if (buttonContainer) {
            buttonContainer.style.display = 'none';
          }
        } else {
          // Re-enable button
          showMoreBtn.disabled = false;
          showMoreBtn.textContent = defaultText;
        }
      }
    } catch (error) {
      console.error('Error loading more products:', error);
      showMoreBtn.disabled = false;
      showMoreBtn.textContent = defaultText;
    }
  };


  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
